/* eslint-disable react/no-array-index-key */

import { memo, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Spacing8, Spacing16, Spacing24 } from 'autogen/design-tokens/tokens';
import type { ProductListResultViewModel } from 'autogen/swagger/all';
import ScrollSnapSlider, { useScrollSnapSlider } from 'components/generic/ScrollSnapSlider';
import useElementSizeEffect from 'hooks/globals/useElementSizeEffect';
import { useIntersectionEffect } from 'hooks/globals/useIntersectionEffect';
import useIsMobile from 'hooks/responsive/useIsMobile';
import { invisibleScrollbar } from 'modules/helpers/mixins';
import { breakpoint, sizes } from 'variables';
import FilterMenuOpener from './FilterMenuOpener';
import RootFilterMenuOpener from './RootFilter/RootFilterMenuOpener';
import SeeAllFiltersRootMenuFilterOpener from './RootFilter/SeeAllFiltersRootMenuFilterOpener';

/* #region styles */
const StyledSeeAllButtonMobile = styled(SeeAllFiltersRootMenuFilterOpener)<{
    $isVisibleMobile: boolean;
    $isVisibleDesktop: boolean;
}>`
    display: ${({ $isVisibleMobile }) => ($isVisibleMobile ? 'flex !important;' : 'none !important;')};
    ${breakpoint.up(sizes.sm)} {
        display: ${({ $isVisibleDesktop }) => ($isVisibleDesktop ? 'flex !important;' : 'none !important;')};
        position: absolute;
        right: ${Spacing16};
    }
`;

const StyledScrollSnapSlider = styled(ScrollSnapSlider)<{ $isOverflowing: boolean; $seeAllButtonWidth?: number }>`
    && {
        position: relative;

        gap: ${Spacing8};
        padding: 0 ${Spacing16};
        margin: 0 -${Spacing16};
        scroll-padding-inline-end: ${Spacing16};
        scroll-padding-inline-start: ${Spacing16};

        & button:nth-child(n + 9) {
            display: none;
        }

        ${invisibleScrollbar()}

        ${breakpoint.up(sizes.sm)} {
            display: inline-flex;
            flex-wrap: wrap;
            max-height: 48px; // height of filter openers

            & > button {
                flex-grow: 1;
            }

            & > button:not(${StyledSeeAllButtonMobile}):nth-child(n + 9) {
                display: flex;
            }
        }

        ${breakpoint.up(sizes.sm)} {
            ${({ $isOverflowing, $seeAllButtonWidth }) => $isOverflowing && `padding-right: calc(${$seeAllButtonWidth}px + ${Spacing24});`}
            display: inline-flex;
            max-height: 48px; // height of filter openers
        }
    }
`;

/* #endregion */

const options = {
    draggable: true,
    loop: false
};

const FilterMenuOpenerRenderer = ({ model }: { model: ProductListResultViewModel }) => {
    const { filters, activeFilterValues } = model;
    const { ref } = useScrollSnapSlider(options);
    const [buttonWidth, setButtonWidth] = useState<number>();

    const aboveFilters = filters.filter((filter) => filter.attribute.location === 'Above');

    // Sort filters so active filter buttons are always displayed first
    const sortedFilters = aboveFilters.sort((a, b) => {
        if (a.isActive && !b.isActive) return -1;
        if (b.isActive && !a.isActive) return 1;
        return 0;
    });

    const isMobile = useIsMobile();
    const [overflowingMenuOpeners, setOverflowingMenuOpeners] = useState(0);

    useIntersectionEffect(
        (entries) => {
            const hiddenMenuOpeners = entries.filter((node) => {
                const isSeeAllButton = node.target.classList.contains('seeAll');

                if (isSeeAllButton) return false;

                return !node.isIntersecting;
            });

            setOverflowingMenuOpeners(hiddenMenuOpeners.length);
        },
        { ref, enable: !isMobile, observeChildren: true },
        [overflowingMenuOpeners, sortedFilters]
    );

    const resetScrollSnapSliderPosition = () => {
        if (ref.current && isMobile) {
            ref.current.scrollLeft = 0;
        }
    };

    const [sizeRef] = useElementSizeEffect<HTMLButtonElement>((size) => {
        setButtonWidth(size.inlineWidth);
    });

    useEffect(() => resetScrollSnapSliderPosition(), [sortedFilters]);

    const seeAllVisibleOnMobile = sortedFilters.length > 8 && !!isMobile; // more than 8 filters on mobile
    const seeAllVisibleOnDesktop = overflowingMenuOpeners > 0; // has overflowing menu openers on desktop

    return (
        <StyledScrollSnapSlider ref={ref} $isOverflowing={seeAllVisibleOnDesktop} $seeAllButtonWidth={buttonWidth}>
            {/* The all Filters menu opener aka. Root filter menu opener */}
            <RootFilterMenuOpener activeFilterValues={activeFilterValues} />

            {/* The specific filter menu openers (brand, price, etc) */}
            {sortedFilters.map((filter) => (
                <FilterMenuOpener key={filter.attribute.id} model={filter} />
            ))}

            {/* See all button  */}
            <StyledSeeAllButtonMobile
                className="seeAll"
                $isVisibleMobile={seeAllVisibleOnMobile}
                $isVisibleDesktop={seeAllVisibleOnDesktop}
                ref={sizeRef}
            />
        </StyledScrollSnapSlider>
    );
};

export default memo(FilterMenuOpenerRenderer);
